/*
 * Copyright (c) 2026. Tooling Team
 */

import PropTypes from "prop-types";
import { Tooltip } from "@uhg-abyss/web/ui/Tooltip";
import {
  formatMiProbabilityValue,
  getMiProbabilityPercentage,
  getMiProbabilityValue,
} from "../utils/formatters";

const SCORE_COLORS = {
  error: "#c63d32",
  warning: "#b7791f",
  success: "#2f7d46",
  track: "#e5e7eb",
};

const CONFIDENCE_SCORE_STYLE = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  margin: "20px 0",
};

const CONFIDENCE_RING_STYLE = {
  alignItems: "center",
  borderRadius: "50%",
  display: "inline-flex",
  height: "112px",
  justifyContent: "center",
  position: "relative",
  width: "112px",
};

const CONFIDENCE_RING_INNER_STYLE = {
  alignItems: "center",
  background: "#fff",
  borderRadius: "50%",
  display: "inline-flex",
  flexDirection: "column",
  height: "100px",
  justifyContent: "center",
  width: "100px",
};

const CONFIDENCE_VALUE_STYLE = {
  fontSize: "22px",
  fontWeight: 700,
  lineHeight: 1,
};

const CONFIDENCE_LABEL_STYLE = {
  color: "#4b5563",
  fontSize: "13px",
  fontWeight: 600,
};

const getScoreColor = (probability) => {
  if (probability === null) return SCORE_COLORS.track;
  if (probability < 0.75) return SCORE_COLORS.error;
  if (probability < 0.9) return SCORE_COLORS.warning;
  return SCORE_COLORS.success;
};

const ConfidenceScore = ({ style, value }) => {
  const probability = getMiProbabilityValue(value);
  const percentage = getMiProbabilityPercentage(value);
  const scoreColor = getScoreColor(probability);
  const displayValue = formatMiProbabilityValue(value, "N/A");
  const tooltipContent =
    percentage === null ? "Confidence score unavailable" : `${percentage}%`;
  const background =
    percentage === null
      ? SCORE_COLORS.track
      : `conic-gradient(${scoreColor} ${percentage}%, ${SCORE_COLORS.track} ${percentage}% 100%)`;

  return (
    <div style={{ ...CONFIDENCE_SCORE_STYLE, ...style }}>
      <Tooltip content={tooltipContent}>
        <div
          aria-label="Confidence score"
          aria-valuemax={100}
          aria-valuemin={0}
          aria-valuenow={
            percentage === null ? undefined : Math.trunc(percentage * 100) / 100
          }
          role="progressbar"
          style={{ ...CONFIDENCE_RING_STYLE, background }}
        >
          <div style={CONFIDENCE_RING_INNER_STYLE}>
            <span style={{ ...CONFIDENCE_VALUE_STYLE, color: scoreColor }}>
              {displayValue}
            </span>
            <span style={CONFIDENCE_LABEL_STYLE}>Confidence</span>
          </div>
        </div>
      </Tooltip>
    </div>
  );
};

ConfidenceScore.propTypes = {
  style: PropTypes.object,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default ConfidenceScore;
